import confetti from "canvas-confetti";
import type { CreateTypes, Options } from "canvas-confetti";
import { MILESTONE_CONFETTI_COLORS } from "../styles/brand";

/** Celebration palette — product ramp + a fleck of gilt, the family's one sanctioned
 * burst of motion (Go Family Bible v2.0, Part 12). Real achievements only. */
export const BOOKING_CONFETTI_COLORS = MILESTONE_CONFETTI_COLORS;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

type FireOptions = {
  /** Bound confetti to a canvas (phone mockup). Defaults to full window. */
  canvas?: HTMLCanvasElement | null;
  durationMs?: number;
};

/**
 * Parent booking celebration — used on real confirm screens and the homepage mockup.
 * No-ops when the user prefers reduced motion (B Law 9).
 */
export function fireBookingConfetti({ canvas, durationMs = 2000 }: FireOptions = {}): () => void {
  if (typeof window === "undefined" || prefersReducedMotion()) {
    return () => undefined;
  }

  const instance: CreateTypes = canvas
    ? confetti.create(canvas, { resize: true, useWorker: true })
    : (confetti as unknown as CreateTypes);

  const end = Date.now() + durationMs;
  const colors = BOOKING_CONFETTI_COLORS;
  let frameId = 0;
  let cancelled = false;

  const frame = () => {
    if (cancelled) return;
    instance({
      particleCount: 7,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.65 },
      colors,
      startVelocity: 35,
      gravity: 0.9,
      ticks: 200,
      zIndex: 200,
    } satisfies Options);
    instance({
      particleCount: 7,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.65 },
      colors,
      startVelocity: 35,
      gravity: 0.9,
      ticks: 200,
      zIndex: 200,
    } satisfies Options);
    if (Date.now() < end) {
      frameId = window.requestAnimationFrame(frame);
    }
  };

  // Opening burst
  instance({
    particleCount: 90,
    spread: 80,
    origin: { x: 0.5, y: 0.45 },
    colors,
    startVelocity: 42,
    zIndex: 200,
  });
  frameId = window.requestAnimationFrame(frame);

  return () => {
    cancelled = true;
    if (frameId) window.cancelAnimationFrame(frameId);
  };
}
